import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import {
  PriceListProductsResponse,
  UpdatePriceListProductParams,
  UpdateProductPositionBody,
} from '@/api/_schemas/management'
import { updateProductPosition } from '@/api/management'
import { ApiError } from '@/config/api'
import { keyStore } from '@/config/key-store'
import { queryClient } from '@/config/query-client'
import { useToast } from '@/hooks/use-toast'
import { handleErrors } from '@/services/fetch'

import { reorderWithSingleUpdate } from '../_utils/reorder-product'

export const useUpdatePriceListProductPosition = (priceListId: number) => {
  const { toastDestructive } = useToast()

  const mutation = useMutation({
    mutationFn: async (
      data: UpdateProductPositionBody &
        UpdatePriceListProductParams & {
          priceListId: number
          fromIndex: number
        },
    ) => await updateProductPosition(data, { id: data.id }),
    onMutate: async (data) => {
      const queryKey = [...keyStore.priceList.products.queryKey, priceListId]

      await queryClient.cancelQueries({ queryKey })

      const previousData =
        queryClient.getQueryData<PriceListProductsResponse>(queryKey)

      if (!previousData) return { previousData }

      const reordered = reorderWithSingleUpdate(
        previousData,
        data.fromIndex,
        data.posicao,
      )

      queryClient.setQueryData(queryKey, reordered)

      return { previousData }
    },
    onError: (error: AxiosError<ApiError>, _data, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [...keyStore.priceList.products.queryKey, priceListId],
          context.previousData,
        )
      }

      handleErrors(error.response?.data, [], toastDestructive)
    },
  })

  return { mutation }
}
